import { createStyles } from "@mantine/core";

// Matches mockup's `.prevlist`/`.prevrow` (previous-note picker) exactly.
const useStyles = createStyles(() => ({
  mymodal: {
    "& .mantine-Drawer-header": {
      backgroundColor: "var(--app-surface)",
      padding: "17px 22px",
      borderBottom: "1px solid var(--app-border)",
      "& .mantine-CloseButton-root": {
        width: "34px !important",
        height: "34px !important",
        borderRadius: "9px !important",
        color: "var(--app-text-muted)",
        "&:hover": {
          backgroundColor: "var(--app-surface-2)",
        },
        "& svg": {
          height: "20px !important",
          width: "20px !important",
        },
      },
    },
    "& .mantine-Drawer-body": {
      padding: "20px !important",
    },
    "& .mantine-Drawer-title": {
      fontSize: "21px",
      fontWeight: "700",
      color: "var(--app-text)",
      fontFamily: "Inter",
    },
    "& .mantine-Drawer-content": {
      borderRadius: "18px 0 0 18px !important",
      backgroundColor: "var(--app-bg)",
      boxShadow: "-8px 0 40px rgba(0,0,0,.25)",
    },
  },
  search: {
    width: "100%",
    height: 44,
    marginBottom: 14,
    border: "1px solid var(--app-border)",
    borderRadius: 8,
    padding: "10px 14px",
    fontFamily: "Inter",
    fontSize: 14,
    color: "var(--app-text)",
    background: "var(--app-surface)",
    outline: "none",
    "&:focus": {
      borderColor: "var(--app-primary)",
      boxShadow: "0 0 0 3px rgba(255,175,74,.2)",
    },
    "&::placeholder": {
      color: "var(--app-text-faint, #8A93A2)",
    },
  },
  prevList: {
    display: "flex",
    flexDirection: "column",
    gap: 10,
  },
  prevRow: {
    display: "flex",
    alignItems: "flex-start",
    gap: 16,
    border: "1px solid var(--app-border)",
    borderRadius: 12,
    padding: "14px 16px",
    background: "var(--app-surface)",
    transition: "border-color .15s, box-shadow .15s",
    "&:hover": {
      borderColor: "var(--app-text-faint, #8A93A2)",
      boxShadow: "0 1px 2px rgba(16,24,40,.05)",
    },
  },
  pmeta: {
    flex: 1,
    minWidth: 0,
  },
  ptop: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: 12,
  },
  ptype: {
    fontFamily: "Inter",
    fontSize: 14.5,
    fontWeight: 650,
    color: "var(--app-text)",
  },
  pdate: {
    fontFamily: "Inter",
    fontSize: 11.5,
    color: "var(--app-text-faint, #8A93A2)",
    whiteSpace: "nowrap",
  },
  pfacts: {
    display: "flex",
    flexDirection: "column",
    gap: 5,
    marginTop: 8,
  },
  pfact: {
    fontFamily: "Inter",
    fontSize: 13,
    color: "var(--app-text)",
    display: "flex",
    alignItems: "baseline",
    minWidth: 0,
    gap: 6,
  },
  pk: {
    flex: "none",
    width: 52,
    fontFamily: "Inter",
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--app-text-faint, #8A93A2)",
  },
  pplan: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: 10,
  },
  pchip: {
    fontFamily: "Inter",
    fontSize: 11.5,
    color: "var(--app-text-muted)",
    background: "var(--app-surface-2)",
    border: "1px solid var(--app-border)",
    borderRadius: 20,
    padding: "3px 11px",
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  pnote: {
    marginTop: 11,
    paddingTop: 11,
    borderTop: "1px dashed var(--app-border)",
  },
  pnk: {
    fontFamily: "Inter",
    fontSize: 9.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.05em",
    color: "var(--app-text-faint, #8A93A2)",
    marginBottom: 3,
  },
  pnt: {
    fontFamily: "Inter",
    fontSize: 12.5,
    color: "var(--app-text-muted)",
    lineHeight: 1.5,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
  },
  // Plain custom button (not Mantine's Button) so sizing matches the
  // mockup's <button class="btn primary sm"> exactly.
  useThisBtn: {
    flex: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    cursor: "pointer",
    backgroundColor: "var(--app-primary)",
    border: "1px solid var(--app-primary)",
    color: "var(--app-on-primary)",
    fontFamily: "Inter",
    fontSize: 12.5,
    fontWeight: 600,
    padding: "7px 11px",
    borderRadius: 9,
    "&:hover": {
      backgroundColor: "#F59E2E",
    },
  },
  emptyState: {
    textAlign: "center",
    color: "var(--app-text-faint, #8A93A2)",
    fontSize: 13,
    padding: "26px 0",
  },
  // Matches mockup's `.dfoot` — attached to the bottom of the drawer, not
  // scrolling with the list. Drawer.Body scrolls with 20px padding on every
  // side (see `mymodal` above), so this sticks to the bottom of that scroll
  // area and cancels the parent's padding with negative margins to sit
  // flush, full-bleed — same technique as the main note drawer's footer.
  footer: {
    display: "flex",
    gap: 12,
    padding: "16px 20px",
    borderTop: "1px solid var(--app-border)",
    background: "var(--app-surface)",
    position: "sticky",
    bottom: 0,
    zIndex: 2,
    marginTop: 14,
    marginLeft: -20,
    marginRight: -20,
    marginBottom: -20,
    width: "calc(100% + 40px)",
  },
  // Plain custom button (not Mantine's Button) matching mockup's
  // <button class="btn"> inside `.dfoot` (flex:1;justify-content:center;padding:13px).
  cancelBtn: {
    flex: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 7,
    cursor: "pointer",
    padding: 13,
    backgroundColor: "var(--app-surface)",
    border: "1px solid var(--app-border)",
    color: "var(--app-text)",
    fontFamily: "Inter",
    fontSize: 13,
    fontWeight: 600,
    borderRadius: 9,
    "&:hover": {
      backgroundColor: "var(--app-surface-2)",
    },
  },
}));

export default useStyles;
